import humps from 'lodash-humps';
import { flatten, cloneDeep, find, findIndex, filter, forEach, groupBy, includes, map, toArray, size, remove, concat, keys, get, split } from 'lodash';
import{ UnifiedService, ServiceTemplateUnderFluUnifiedService, MetadataRequiredOverridable, ServiceSettings, GlobalSettings } from '@/enums';
import Vue from 'vue';

const state = {
    instantiateServices: [],
    associatedEst: [],
    dynamicServiceFilter: [],
    allMetaData: {},
    selectedServiceMetadataValues: {},
    selectedService: '',
    isSelectedService: false,
    isFirstServiceLoad: false,
    unifiedService: null,
    currentEstMetas: {},
    currentEstSettings: {},
    selectedServiceConfigurationMessages: {},
    serviceHasDynamicFilter: false,
    dynamicFilterMetadataValues: [],
    serviceHasDataCollectionFormId: null,
    clientFiltersIsAdmissible: true,
    portalEst: null,
    serviceHasServiceTemplateFilterIds: false,
    servicesFilteredByServiceTemplateIds: [],
};

const getters = {
    getPortalEst: state => {
        return state.portalEst;
    },

    getDynamicFilterMetadataValues: state => {
        return state.dynamicFilterMetadataValues;
    },

    getInstantiateServiceById: state => id => {
        return find(state.instantiateServices, { id: parseInt(id) });
    },

    getServiceByID: state => {
        return groupBy(state.instantiateServices, 'id');
    },

    getInstantiateServices: (state) => {
        return state.instantiateServices || [];
    },

    getInstantiateServiceTemplatesIds: (state) => {
        return map(state.instantiateServices, s => {
            return s.serviceTemplate.id;
        });
    },

    getUnifiedServices: (state) => state.unifiedService,
    getCurrentEstMetas: (state) => state.currentEstMetas,
    getCurrentEstSettings: (state) => state.currentEstSettings,

    getInstantiatedService: state => id => {
        return find(state.instantiateServices, ['id', id]);
    },

    getActivatedServices: (state) => {
        return filter(state.instantiateServices, ['status', 1]);
    },

    getAssociatedEst: (state) => {
        return state.associatedEst;
    },

    getFirstServiceLoad: (state) => {
        return state.isFirstServiceLoad;
    },

    getBaseMetadataById: state => id => {
        return state.allMetaData[id];
    },

    getCopySelectedServiceMetadataValues: state => {
        return cloneDeep(state.selectedServiceMetadataValues);
    },

    getCopySelectedServiceMetadataValuesOverrideApplied: (state, getters, rootState, rootGetters) => {
        const metadataValues = cloneDeep(getters.getCopySelectedServiceMetadataValues);

        forEach(metadataValues, (m) => {
            // Retrieve base metadata object
            const baseMetadata = getters.getBaseMetadataById(m.metadata);

            // Set the isOverride prop to false by default
            m.isOverride = false;

            // Can metadata be overridden in public ?
            const metadataOverridable = baseMetadata.requiredOverridable === MetadataRequiredOverridable.OVERRIDABLE_PUBLIC_ADMIN;

            // If user can override, set required to false and isOverride to true, else leave it intact
            if (metadataOverridable && m.required) {
                m.required = false;
                m.isOverride = true;
            }
        });

        return metadataValues;
    },

    getOrderredCopySelectedServiceMetadataValuesOverrideAppliedKeys: (state, getters, rootState, rootGetters) => {
        let baseMetadatas = getters.getCopySelectedServiceMetadataValuesOverrideApplied;
        let baseMetadatasKeys = keys(baseMetadatas);
        let orderedMetadataKeys = [];
        const customDisplayOrderFlag = get(rootGetters['globalSettings/getGlobalSettings'], GlobalSettings.METADATA_DISPLAY_ORDER_FEATURE_FLAG);
        let customDisplayOrder = customDisplayOrderFlag && customDisplayOrderFlag.value ? find(getters.getCurrentEstSettings, ['setting', ServiceSettings.METADATA_DISPLAY_ORDER]) : [];

        // Order from EST settings.
        forEach(customDisplayOrder.value, serviceId => {
            // I prefer recreating a new array of keys from the baseMetadatasKeys in case customDisplayOrder have id's that we haven't received.
            orderedMetadataKeys = concat(orderedMetadataKeys, remove(baseMetadatasKeys, (key) => key === serviceId.toString()));
        });

        // All the rest that didn't have order.
        orderedMetadataKeys = concat(orderedMetadataKeys, baseMetadatasKeys);

        return orderedMetadataKeys;
    },

    getOrderredCopySelectedServiceMetadataValuesOverrideApplied: (state, getters, rootState, rootGetters) => {
        let baseMetadatas = getters.getCopySelectedServiceMetadataValuesOverrideApplied;
        let orderedMetadataKeys = getters.getOrderredCopySelectedServiceMetadataValuesOverrideAppliedKeys;
        let arrayMetaOrdered = [];
        for (let i = 0; i < orderedMetadataKeys.length; i++) {
            let currentMetadata = baseMetadatas[orderedMetadataKeys[i]];
            if(currentMetadata) {
                arrayMetaOrdered.push(baseMetadatas[orderedMetadataKeys[i]]);
            }
        }

        return arrayMetaOrdered;
    },

    getDynamicServiceFilters: (state) => {
        return state.dynamicServiceFilters || [];
    },

    serviceHasDynamicFilter: (state, getters, rootState, rootGetters) => {
        return size(rootGetters['services/getDynamicServiceFilters']);
    },

    getServiceHasModule: (state, getters, rootState, rootGetters) => (payload) => {
        const [moduleName, serviceModuleId] = payload;
        if (serviceModuleId) {
            const allModules = rootGetters['establishment/getAllModules'];
            const index = findIndex(allModules, ['name', moduleName]);
            const moduleId = index + 1 && allModules[index] ? allModules[index].id : null;
            return serviceModuleId === moduleId;
        }
        return false;
    },

    getSelectedService: state => {
        return state.selectedService;
    },

    getSelectedServiceId: state => {
        return state.selectedService
            ? state.selectedService.id
            : null;
    },

    getIsSelectedService: state => {
        return state.isSelectedService;
    },

    getSelectedServiceConfigurationMessageById: state => id => {
        return state.selectedServiceConfigurationMessages[id];
    },

    getIsFluService: state => {
        return state.isFluService;
    },

    getServiceHasDataCollectionFormId: state => {
        return state.serviceHasDataCollectionFormId;
    },

    getClientFiltersIsAdmissible: state => {
        return state.clientFiltersIsAdmissible;
    },

    getServiceHasServiceTemplateFilterIds: state => {
        return state.serviceHasServiceTemplateFilterIds;
    },

    getServicesFilteredByServiceTemplateIds: state => {
        return state.servicesFilteredByServiceTemplateIds;
    },
};

const mutations = {
    SET_PORTAL_EST(state, payload) {
        state.portalEst = parseInt(payload);
    },
    SET_DYNAMIC_FILTER_METADATA_VALUES(state, payload) {
        state.dynamicFilterMetadataValues = payload || [];
    },
    SET_INSTANTIATE_SERVICES(state, payload) {
        state.instantiateServices = payload || [];
    },
    SET_ASSOCIATED_EST(state, payload) {
        state.associatedEst = payload || [];
    },
    SET_UNIFIED_SERVICE(state, payload) {
        state.unifiedService = payload;
    },
    SET_CURRENT_EST_METAS(state, payload) {
        state.currentEstMetas = payload;
    },
    SET_CURRENT_EST_SETTINGS(state, payload) {
        state.currentEstSettings = payload;
    },
    SET_DYNAMIC_SERVICE_FILTERS(state, payload) {
        state.dynamicServiceFilters = payload || [];
    },
    SET_ALL_METADATA(state, payload) {
        state.allMetaData = {};
        forEach(payload, (m) => { Vue.set(state.allMetaData, m.id, m); });
    },
    SET_SELECTED_SERVICE_METADATA_VALUES(state, payload) {
        state.selectedServiceMetadataValues = {};
        forEach(payload, (m) => { Vue.set(state.selectedServiceMetadataValues, m.metadata, m); });
    },
    SET_SELECTED_SERVICE(state, payload) {
        state.selectedService = payload;
    },
    SET_IS_SELECTED_SERVICE(state, payload) {
        state.isSelectedService = payload;
    },
    SET_SELECTED_SERVICE_CONFIGURATION_MESSAGES(state, payload) {
        state.selectedServiceConfigurationMessages = {};
        forEach(payload, (cm) => { Vue.set(state.selectedServiceConfigurationMessages, cm.configurationMessage, cm); });
    },
    SET_FIRST_SERVICE_LOAD(state, payload) {
        state.isFirstServiceLoad = payload;
    },
    SET_SERVICE_HAS_DATA_COLLECTION_FORM_ID(state) {
        if (get(state.selectedService, 'settings')) {
            const dataCollectionFormId = find(state.selectedService.settings, ['setting', ServiceSettings.ENABLE_ESTABLISHMENT_SERVICE_TEMPLATES_FORMS], 0);
            state.serviceHasDataCollectionFormId = get(dataCollectionFormId, 'value', null);
        }
        else {
            state.serviceHasDataCollectionFormId = null;
        }
    },

    SET_CLIENT_FILTERS_IS_ADMISSIBLE(state, payload) {
        state.clientFiltersIsAdmissible = payload;
    },

    SET_SERVICE_HAS_SERVICE_TEMPLATE_FILTER_IDS(state, payload) {
        state.serviceHasServiceTemplateFilterIds = payload;
    },

    SET_SERVICES_FILTERED_BY_SERVICE_TEMPLATE_IDS(state, payload) {
        state.servicesFilteredByServiceTemplateIds = payload;
    },

    RESET_SERVICES_FILTERED_BY_SERVICE_TEMPLATE_IDS(state) {
        state.serviceHasServiceTemplateFilterIds = false;
        state.servicesFilteredByServiceTemplateIds = [];
    },
};

const actions = {
    setServicesFilteredByServiceTemplateIds({ commit }, payload) {
        let serviceTemplates = split(payload, ',');
        const servicesTemplatesFiltered = flatten(map(serviceTemplates, s => {
            return filter(state.instantiateServices, ['serviceTemplate.id', parseInt(s)]);
        }));
        if (servicesTemplatesFiltered.length > 0) {
            commit('SET_SERVICE_HAS_SERVICE_TEMPLATE_FILTER_IDS', true);
        }
        commit('SET_SERVICES_FILTERED_BY_SERVICE_TEMPLATE_IDS', servicesTemplatesFiltered);
        commit('SET_FIRST_SERVICE_LOAD', true);
    },

    setPortalEst({ commit }, payload) {
        commit('SET_PORTAL_EST', payload);
    },
    /**
     * @description Fetch All Metas from the API
     * @param commit
     * @returns {Q.Promise<any>}
     */
    getAllMetaData({ commit }) {
        return this._vm.$api.services.getAllMetaData()
            .then(response => {
                const arr = toArray(humps(response.data));

                if (!arr.length) {
                    throw new Error('No meta data found');
                }
                commit('SET_ALL_METADATA', humps(arr));
            });
    },

    /**
     * @description Fetch Establishment Service Template metas from the API
     * @param commit
     * @param getters
     * @param rootGetters
     * @returns {Q.Promise<any>}
     */
    async getEstMetaData({ commit, getters, rootGetters }) {
        const establishmentId = rootGetters['establishment/getEstablishmentId'];
        const estId = getters.getSelectedServiceId;
        if(estId) {
            const response = await this._vm.$api.services.getEstMetadata(establishmentId, estId);
            const data = humps(response.data);
            commit('SET_CURRENT_EST_METAS', data);
        }
    },

    async fetchEstMetadataValues({ commit, rootGetters }, estId) {
        const establishmentId = rootGetters['establishment/getEstablishmentId'];
        const response = await this._vm.$api.services.getEstMetadata(establishmentId, estId);
        commit('SET_SELECTED_SERVICE_METADATA_VALUES', humps(response.data));
    },

    /**
     * @description Fetch Establishment Service Template settings from the API
     * @param commit
     * @param getters
     * @param rootGetters
     * @returns {Q.Promise<any>}
     */
    async getEstSettings({ commit, getters, rootGetters }) {
        const establishmentId = rootGetters['establishment/getEstablishmentId'];
        const estId = getters.getSelectedServiceId;
        if(estId) {
            const response = await this._vm.$api.services.getEstSettings(establishmentId, estId);
            const data = humps(response.data);
            commit('SET_CURRENT_EST_SETTINGS', data);
        }
    },

    /**
     * @description Fetch Instanciated service from the API
     * @param commit
     * @param dispatch
     * @param getters
     * @param rootGetters
     * @param rootDispatch
     * @returns {Q.Promise<any>}
     */
    async getInstantiateServices({ commit, dispatch, getters, rootGetters }) {
        const estabId = rootGetters['establishment/getEstablishmentId'];
        const response = await this._vm.$api.services.getAllByEstablishment(estabId, null, false, true);
        const data = humps(response.data);
        commit('SET_INSTANTIATE_SERVICES', data);

        // Auto-select a service when the service_unified is flu
        if(getters.getUnifiedServices === UnifiedService.FLU) {
            const serviceFlu = find(getters.getActivatedServices, d => {
                return d && d.serviceTemplate &&
                includes(ServiceTemplateUnderFluUnifiedService, d.serviceTemplate.id);
            });

            if(serviceFlu) {
                commit('SET_SELECTED_SERVICE', serviceFlu);
                await Promise.all([
                    dispatch('getEstMetaData'),
                    dispatch('getEstSettings'),
                ]);
            }
        }

        // Auto-select a service when there is only one service available
        else if(getters.getActivatedServices.length === 1) {
            commit('SET_SELECTED_SERVICE', getters.getActivatedServices[0]);
            await Promise.all([
                dispatch('getEstMetaData'),
                dispatch('getEstSettings'),
            ]);
        }
    },

    async getEstServiceUnifiedAssociationsByServiceUnified({ commit, rootGetters }, { establishmentId, serviceUnifiedId }) {
        const response = await this._vm.$api.services.getEstServiceUnifiedAssociationsByServiceUnified(establishmentId, serviceUnifiedId);
        const data = humps(response.data);
        commit('SET_ASSOCIATED_EST', data);
        commit('SET_FIRST_SERVICE_LOAD', true);
    },

    /**
     * @description Fetch a service Filter from the API
     * @param commit
     * @param rootGetters
     * @param establishmentId
     * @param unifiedServiceId
     * @param ignoreUnifiedServiceSet
     * @returns {Q.Promise<any>}
     */
    getDynamicServiceFilters({ commit, rootGetters }, { establishmentId, unifiedServiceId, ignoreUnifiedServiceSet = false }) {
        if(!ignoreUnifiedServiceSet) commit('SET_UNIFIED_SERVICE', parseInt(unifiedServiceId));
        return this._vm.$api.services.getServiceTemplatesFilters(establishmentId, unifiedServiceId)
            .then((response) => {
                commit('SET_DYNAMIC_SERVICE_FILTERS', humps(response.data));
            });
    },

    /**
     * @description Fetch configuration messages for an establishment service template
     * @param commit
     * @param rootGetters
     * @param estId
     * @returns {Promise<void>}
     */
    async fetchEstConfigurationMessages({ commit, rootGetters }, estId) {
        const establishmentId = rootGetters['establishment/getEstablishmentId'];
        const response = await this._vm.$api.services.getEstConfigurationMessages(establishmentId, estId);
        commit('SET_SELECTED_SERVICE_CONFIGURATION_MESSAGES', humps(response.data));
    },

    async removeInstantiateServiceWithServiceTemplateIds({ getters, commit }, serviceTemplateIds) {
        const services = getters.getInstantiateServices;
        const filteredServices = filter(services, s => {
            return !includes(serviceTemplateIds, s.serviceTemplate.id);
        });
        commit('SET_INSTANTIATE_SERVICES', filteredServices);
    },

    async filterInstantiateServiceWithServiceTemplateIds({ getters, commit }, serviceTemplateIds) {
        const services = getters.getInstantiateServices;
        const filteredServices = filter(services, s => {
            return includes(serviceTemplateIds, s.serviceTemplateTag);
        });
        commit('SET_INSTANTIATE_SERVICES', filteredServices);
    },

    async setInstantiateServices({ getters, commit }, serviceTemplateIds) {
        commit('SET_INSTANTIATE_SERVICES', serviceTemplateIds);
    },

    async setSelectedPortalEST({ getters, commit }) {
        commit(
            'SET_SELECTED_SERVICE',
            find(getters.getActivatedServices, ['id', getters.getPortalEst])
        );
    }
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
